// npm i js-yaml
import YAML from 'js-yaml';

/**
 * Build the "Allocation" YAML expected by /jobmanager/groups.
 *
 * Modes:
 *  - "default" (everyone else): component.type = "manifest"
 *  - "tma"     (tbB users):     component.type = "kubernetes"
 *
 * Notes:
 *  - We support single or multi-doc YAML uploads per job.
 *  - For default mode we keep enforcing metadata.name = <manifestName w/o ext>
 *    and metadata.namespace = <appName> to match previous behavior.
 *  - For TMA mode we do NOT force names (their CRDs may require specific values).
 *
 * @param {Object} resp  getApplicationInformation(...) shape
 * @param {Object} opts  { mode: "default"|"tma", cluster_name, node_name, orchestrator }
 * @returns {string} YAML text
 */
export function buildAllocationYAML(resp, opts = {}) {
  const mode = opts.mode === 'tma' ? 'tma' : 'default';

  const appName    = resp.applicationName || 'app';
  const namespace  = appName; // keep same convention as before
  const orchestrator = opts.orchestrator || 'ocm';
  const clusterName  = opts.cluster_name || 'kind-qos';
  const nodeName     = opts.node_name ?? 'any';

  const headerNameOf = (manifestName) =>
    String(manifestName || '')
      .trim()
      .replace(/\.(ya?ml)$/i, '');

  // ── 1) Components
  const components = (resp.components || []).map(c => ({
    name: c.name,
    // default shape used by INTRA; TMA needs "kubernetes"
    type: mode === 'tma' ? 'kubernetes' : 'manifest',
    manifests: (c.jobs || []).map(j => ({
      name: headerNameOf(j.manifestName)
    })),
    targets: {
      cluster_name : clusterName,
      orchestrator : orchestrator,
      node_name    : nodeName,
    }
  }));

  // ── 2) Manifests: decode each uploaded YAML (support multi-doc)
  const manifests = [];
  for (const c of resp.components || []) {
    for (const j of c.jobs || []) {
      const bytes = (j.manifestFile && j.manifestFile.data) || [];
      if (!bytes.length) continue;

      const yamlText = new TextDecoder().decode(new Uint8Array(bytes));

      // Load ALL docs (--- separated). Fallback to [] if malformed.
      let docs = [];
      try {
        docs = YAML.loadAll(yamlText).filter(Boolean);
      } catch (e) {
        // push a minimal stub so submit flow can still surface error up the stack
        docs = [];
      }

      for (const obj of docs) {
        if (obj && typeof obj === 'object') {
          // Ensure metadata exists when it's a k8s-style resource
          if (!obj.metadata) obj.metadata = {};

          if (mode === 'default') {
            // keep previous behavior for INTRA/default:
            // enforce namespace & a predictable name (headerName)
            obj.metadata.namespace = namespace;
            const expectedName = headerNameOf(j.manifestName);
            if (expectedName) obj.metadata.name = expectedName;
          } else {
            // TMA mode: do NOT override names/namespaces
            // (their CRDs may require specific values)
          }

          manifests.push(obj);
        }
      }
    }
  }

  // ── 3) Allocation root object
  const allocation = {
    name       : appName,
    description: resp.applicationDescription || '',
    namespace,
    components,
    manifests
  };

  return YAML.dump(allocation, { lineWidth: 120 });
}
